import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/ui/design-system/components/ui/select';
import { useEffect, useId } from 'react';
import { AiGeneratedLabel } from '@/ui/design-system/components';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/ui/design-system/components/ui/button';
import { cn } from '@/ui/design-system/utils/cn';
import { views } from '@/copy';

const AssistantComposer = ({
    value,
    onValueChange,
    onSubmit,
    onKeyDown,
    isLoading,
    modelTier,
    setModelTier,
    placeholder = 'Ask about trends, brands, ingredients...',
    maxLength,
    textareaRef,
    showFreePreview = true,
    showModelSelector = true,
    // EU AI Act Art. 50(1) under the box: the panel's sentence, an
    // "AI-generated" label, or none where the placeholder names Spate AI.
    disclosure = 'notice',
    className,
    // Optional overrides for the composer surface (the bordered card). Lets a
    // host page restyle it — e.g. the Reports page makes it a white card with a
    // focus ring so it pops on a gray background instead of blending in.
    surfaceClassName,
}) => {
    const disclaimerId = useId();

    // Resize on every value change (not just onChange) so programmatic fills —
    // e.g. selecting a use-case pill — grow the textarea to fit.
    useEffect(() => {
        const el = textareaRef?.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [value, textareaRef]);

    const handleInput = (e) => {
        onValueChange(e.target.value);
    };

    // The textarea only covers the top of the card; the footer row (around the
    // model select + send button) is dead space that looks clickable but isn't.
    // Route clicks on that whitespace to the textarea — but leave the controls
    // (and the textarea's own caret placement) alone.
    const handleSurfaceMouseDown = (e) => {
        if (e.target.closest('button, textarea, [role="combobox"]')) return;
        e.preventDefault(); // keep focus/selection from shifting, then place it
        textareaRef?.current?.focus();
    };

    return (
        <div className={cn('p-4', className)}>
            <div
                onMouseDown={handleSurfaceMouseDown}
                className={cn(
                    'border border-input rounded-lg bg-[#fafafa] shadow-xs overflow-hidden',
                    surfaceClassName,
                )}
            >
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={value}
                    onChange={handleInput}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    aria-label="Message Spate AI"
                    aria-describedby={
                        disclosure === 'none' ? undefined : disclaimerId
                    }
                    maxLength={maxLength}
                    className="w-full border-none bg-transparent px-3 pt-2 pb-1 text-sm outline-none resize-none"
                    style={{ minHeight: '40px', maxHeight: '160px' }}
                />
                <div
                    className={cn(
                        'flex items-center px-3 pb-2',
                        showModelSelector ? 'justify-between' : 'justify-end',
                    )}
                >
                    {showModelSelector && (
                        <Select
                            value={modelTier ?? 'auto'}
                            onValueChange={(val) =>
                                setModelTier(val === 'auto' ? null : val)
                            }
                            disabled={isLoading}
                        >
                            <SelectTrigger className="h-9 w-auto gap-2 border-input bg-background text-sm font-medium px-4 py-2 rounded-md">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Auto</SelectItem>
                                <SelectItem value="fast">Fast</SelectItem>
                                <SelectItem value="capable">Capable</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Button
                        variant="default"
                        size="icon"
                        className="h-9 w-9 rounded-lg"
                        onClick={onSubmit}
                        disabled={!value.trim()}
                        aria-label="Send message"
                    >
                        <ArrowUp size={18} />
                    </Button>
                </div>
            </div>
            {disclosure !== 'none' || showFreePreview ? (
                <div className="mt-3 flex items-start justify-between gap-3">
                    {disclosure === 'notice' && (
                        <p
                            id={disclaimerId}
                            className="m-0 text-[11px] leading-4 text-muted-foreground"
                        >
                            {views.assistant.disclaimer}
                        </p>
                    )}
                    {disclosure === 'label' && (
                        <AiGeneratedLabel id={disclaimerId} size="sm" />
                    )}
                    {showFreePreview ? (
                        <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] leading-4 text-muted-foreground">
                            Free Preview
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default AssistantComposer;
